// Chart screenshots on trades: added in the trade window (pick a file, drag it in or
// paste it), shown as small previews in the calendar's day pop-up, and opened full
// size on click. Images are shrunk in the browser, then uploaded straight to storage.
import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from './api';
import { translateNow } from './langState';
import { useI18n } from './i18n';

export const MAX_SHOTS = 4;

// Draws the image at most `maxWidth` wide and returns it as a JPEG.
async function shrink(file, maxWidth, quality) {
  let source;
  try {
    source = await createImageBitmap(file);
  } catch {
    source = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  }
  const scale = Math.min(1, maxWidth / source.width);
  const width = Math.round(source.width * scale);
  const height = Math.round(source.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#040d1f';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(source, 0, 0, width, height);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
  if (!blob) throw new Error('encode');
  return { blob, width, height };
}

async function putFile(url, blob) {
  const res = await fetch(url, { method: 'PUT', headers: { 'Content-Type': 'image/jpeg' }, body: blob });
  if (!res.ok) throw new ApiError(translateNow("The upload didn't finish. Please try again."), res.status);
}

// Uploads one image for a trade and returns the updated trade.
export async function uploadScreenshot(tradeId, file) {
  if (!file || !/^image\//.test(file.type)) throw new ApiError(translateNow('That file isn’t an image. Try a PNG or JPG screenshot.'), 400);
  let full;
  let thumb;
  try {
    full = await shrink(file, 1920, 0.85);
    thumb = await shrink(file, 480, 0.8);
  } catch {
    throw new ApiError(translateNow('Couldn’t read that image. Try a PNG or JPG screenshot.'), 400);
  }
  const links = await api('/screenshots/upload-url', { method: 'POST', body: { tradeId } });
  await Promise.all([putFile(links.full, full.blob), putFile(links.thumb, thumb.blob)]);
  const trade = await api('/screenshots/confirm', { method: 'POST', body: { tradeId, id: links.id, width: full.width, height: full.height } });
  return trade;
}

// Signed links to view screenshots, refreshed well before they expire.
const cache = new Map();
const FRESH_MS = 45 * 60000;

export function useScreenshotUrls(trades) {
  const wanted = trades.filter((t) => t.screenshots && t.screenshots.length);
  const signature = wanted.map((t) => `${t._id}:${t.screenshots.map((s) => s.id).join(',')}`).join('|');
  const [urls, setUrls] = useState({});

  useEffect(() => {
    if (!wanted.length) {
      setUrls({});
      return undefined;
    }
    let cancelled = false;
    const now = Date.now();
    const known = {};
    const missing = [];
    for (const t of wanted) {
      const hit = cache.get(t._id);
      const ids = t.screenshots.map((s) => s.id).join(',');
      if (hit && now - hit.at < FRESH_MS && hit.ids === ids) known[t._id] = hit.shots;
      else missing.push(t._id);
    }
    setUrls(known);
    if (missing.length) {
      api('/screenshots/view', { method: 'POST', body: { tradeIds: missing } })
        .then((result) => {
          for (const [id, shots] of Object.entries(result)) {
            cache.set(id, { at: Date.now(), shots, ids: shots.map((s) => s.id).join(',') });
          }
          if (!cancelled) setUrls((prev) => ({ ...prev, ...result }));
        })
        .catch(() => {});
    }
    return () => {
      cancelled = true;
    };
  }, [signature]); // eslint-disable-line react-hooks/exhaustive-deps

  return urls;
}

export function forgetScreenshotUrls(tradeId) {
  cache.delete(tradeId);
}

// A full-size viewer with previous/next for one trade's screenshots.
export function ShotViewer({ shots, start, caption, onClose }) {
  const { t } = useI18n();
  const [index, setIndex] = useState(start);
  const step = useCallback((d) => setIndex((i) => (i + d + shots.length) % shots.length), [shots.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, step]);

  const shot = shots[index];
  return (
    <div className="lightbox shot-viewer" role="dialog" aria-modal="true" aria-label={caption} onClick={onClose}>
      <button type="button" className="lightbox-close" onClick={onClose} aria-label={t('Close')}>&times;</button>
      <figure onClick={(e) => e.stopPropagation()}>
        <img src={shot.full} alt={caption} width={shot.width} height={shot.height} />
        <figcaption>
          {shots.length > 1 && <button type="button" className="secondary small" onClick={() => step(-1)} aria-label={t('Previous')}>&larr;</button>}
          <span>{caption}{shots.length > 1 ? ` · ${index + 1}/${shots.length}` : ''}</span>
          {shots.length > 1 && <button type="button" className="secondary small" onClick={() => step(1)} aria-label={t('Next')}>&rarr;</button>}
        </figcaption>
      </figure>
    </div>
  );
}

// Thumbnails for one trade, clickable to open the viewer.
export function ShotThumbs({ shots, caption, onRemove, busyId }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(null);
  if (!shots || !shots.length) return null;
  return (
    <>
      <ul className="shot-thumbs">
        {shots.map((s, i) => (
          <li key={s.id}>
            <button type="button" className="shot-thumb" onClick={() => setOpen(i)} aria-label={t('Open screenshot {n}', { n: i + 1 })}>
              {s.thumb ? <img src={s.thumb} alt="" loading="lazy" /> : <span className="shot-loading" />}
            </button>
            {onRemove && (
              <button type="button" className="shot-remove" onClick={() => onRemove(s.id)} disabled={busyId === s.id}
                aria-label={t('Remove screenshot {n}', { n: i + 1 })} title={t('Remove')}>&times;</button>
            )}
          </li>
        ))}
      </ul>
      {open !== null && shots[open] && shots[open].full && (
        <ShotViewer shots={shots.filter((s) => s.full)} start={open} caption={caption} onClose={() => setOpen(null)} />
      )}
    </>
  );
}

// The screenshot section of the trade window: previews, add (file, drop or paste) and remove.
export function TradeScreenshots({ trade, onTradeUpdated, onSessionExpired }) {
  const { t } = useI18n();
  const urls = useScreenshotUrls([trade]);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(false);
  const count = (trade.screenshots || []).length;
  const shots = (trade.screenshots || []).map((s) => (urls[trade._id] || []).find((u) => u.id === s.id) || { id: s.id });

  const add = useCallback(async (files) => {
    const list = [...files].filter((f) => /^image\//.test(f.type)).slice(0, MAX_SHOTS - count);
    if (!list.length) {
      if (files.length) setError(t('That file isn’t an image. Try a PNG or JPG screenshot.'));
      return;
    }
    setError('');
    setBusy(true);
    let current = trade;
    try {
      for (const file of list) {
        current = await uploadScreenshot(current._id, file); // eslint-disable-line no-await-in-loop
      }
      forgetScreenshotUrls(current._id);
      onTradeUpdated(current);
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setError(err.message);
      if (current !== trade) onTradeUpdated(current);
    } finally {
      setBusy(false);
    }
    return undefined;
  }, [count, trade, onTradeUpdated, onSessionExpired, t]);

  // Paste a screenshot straight from the clipboard while the trade window is open.
  useEffect(() => {
    const onPaste = (e) => {
      const files = [...(e.clipboardData ? e.clipboardData.files : [])];
      if (files.some((f) => /^image\//.test(f.type)) && count < MAX_SHOTS && !busy) {
        e.preventDefault();
        add(files);
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [add, count, busy]);

  async function remove(id) {
    setRemoving(id);
    setError('');
    try {
      const updated = await api(`/screenshots/${trade._id}/${id}`, { method: 'DELETE' });
      forgetScreenshotUrls(trade._id);
      onTradeUpdated(updated);
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setError(err.message);
    } finally {
      setRemoving(null);
    }
    return undefined;
  }

  return (
    <div
      className={`modal-section trade-shots${dragging ? ' dragging' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); add(e.dataTransfer.files); }}
    >
      <div className="modal-section-head">
        <span className="modal-section-label">{t('Chart screenshots')}</span>
        <span className="field-hint">{t('{n} of {max}', { n: count, max: MAX_SHOTS })}</span>
      </div>
      <ShotThumbs shots={shots} caption={`${trade.symbol} · ${new Date(trade.date).toLocaleDateString()}`} onRemove={remove} busyId={removing} />
      {count < MAX_SHOTS && (
        <label className={`shot-add${busy ? ' busy' : ''}`}>
          <input type="file" accept="image/*" multiple onChange={(e) => { add(e.target.files); e.target.value = ''; }} disabled={busy} />
          <span>{busy ? t('Uploading…') : t('+ Add a screenshot')}</span>
          <span className="field-hint">{t('Or drag an image here, or paste one with Ctrl+V / Cmd+V.')}</span>
        </label>
      )}
      {error && <p className="error">{error}</p>}
    </div>
  );
}
